import React, { useContext, useEffect, useState } from 'react';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Link from 'next/link';
import { Button } from 'react-bootstrap';
import { useRouter } from 'next/router';

import { SellerForm } from '../../components/marketing-site/marketing-utils';
import AnimatedComponent from '../../components/marketing-site/animated-component/AnimatedComponent';
import MarketingLayout from '../../components/marketing-site/MarketingLayout';
import { ModalPortal } from '../../lib/modal/ModalPortal';
import { useLoggedIn } from '../../lib/user-logged-in/UserLoggedIn';
import { UserContext, UserType } from '../../contexts/user/UserContext';
import { CONTACT_REASONS } from '../../lib/interfaces/contact-form/reasons';
import { NextSeo } from 'next-seo';
import type { GetServerSideProps } from "next";
import absoluteUrl from 'next-absolute-url';
import axios from 'axios';
import { initPath } from '../../lib/api/Api';

export interface ISellerProps {
    initData: any;
}

export const getServerSideProps = (async ({req}) => {
    const {origin} = absoluteUrl(req);

    const initDataResponse = await axios({
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8'
        },
        url: `${origin}${initPath}`
    });

    return {
        props: {
           initData: initDataResponse.data
        }
    }
}) satisfies GetServerSideProps<ISellerProps>

const Seller: React.FC = () => {
  const router = useRouter();
  const {logout} = useLoggedIn();
  const {user} = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const sellerForms = document.querySelectorAll('.seller-form');
    sellerForms?.forEach(form => new SellerForm(form, router));
  }, [])

  return (
    <>
        <NextSeo
            title="Sell Your Car Easily & Get the Best Offer | Purr"
            description="Effortlessly sell your car at the best price with Purr. Let the experts handle it all. Get more money hassle-free."
        />
        <MarketingLayout>
            <section className="bg-gray masthead seller">
            <div className="container custom-container">
                <div className="masthead-wrapper">
                <div className="content">
                    <h1>Sit back and sell your vehicle<br /> at full market price.</h1>
                    <div>
                    <form className="seller-form">
                        <label className="mt-3 mb-3"><strong>What vehicle are you selling?</strong></label>
                        <div>
                        <label>Year</label>
                        <select data-testid={'year-select'} className="form-select" data-type="year" defaultValue="" required>
                            <option disabled value="" hidden>Select Year</option>
                        </select>
                        </div>
                        <div className="d-flex flex-column flex-md-row gap-3 mb-3 mt-3">
                        <div className="w-100">
                            <label>Make</label>
                            <select data-testid={'make-select'}  className="form-select" data-type="make" defaultValue="" required>
                            <option disabled value="" hidden>Select Make</option>
                            </select>
                        </div>
                        <div className="w-100">
                            <label>Model</label>
                            <select data-testid={'model-select'}  className="form-select" data-type="model" defaultValue="" required>
                            <option disabled value="" hidden>Select Model</option>
                            </select>
                        </div>
                        </div>
                        <div className="mt-4">
                        <button data-testid={'get-started'} type="submit" data-type="submit-button" onClick={(event) => {
                            if (user?.type === UserType.DEALER) {
                            event.preventDefault();
                            event.stopPropagation();
                            setIsOpen(true);
                            }
                        }} id='btn' className="w-100 btn btn-purr me-3">Get started</button>
                        </div>
                        <Button
                        variant='link'
                        className='link-button w-100 mt-2'
                        onClick={() => {router.push(`/contact-form?type=${CONTACT_REASONS.MISSING_VEHICLE_OPTION}`)}}>
                        Can't find your vehicle?
                        </Button>
                        <ModalPortal
                            className="modalDeleteRed"
                            show={isOpen}
                            showFooter={true}
                            onCloseHandler={() => {
                            setIsOpen(false);
                            }}
                            onSave={() => {
                            logout?.();
                            router.push('/signin').then(() => {
                                router.reload();
                            });
                            setIsOpen(false);
                            }}
                            heading={'Warning'}
                            saveLabel="Logout and continue"
                            closeLabel="Cancel">
                        <div className="px-3">
                            You are currently signed in as a Dealer user. You must sign out in order to continue.
                        </div>
                        </ModalPortal>
                    </form>
                    </div>
                </div>
                <div className="hero-container">
                    <div className="circle"></div>
                    <div className="hero">
                    <AnimatedComponent path='/marketing-assets/animations/heros/seller.json'/>
                    </div>
                </div>
                </div>
            </div>
            </section>
            <section className="bg-gray challenges above-cat">
            <div className="container custom-container">
                <h2 className="alt-header mb-5">How sellers win with <span className="underline">Purr</span></h2>
                <div className="stats">
                <div className="stats-item">
                    <div>
                    <h4>The only sold-for-you marketplace</h4>
                    <img className="stat-image" src="/marketing-assets/images/icons/checklist.svg" />
                    <p className="font-graphik">
                        Purr is the only marketplace where you simply list your car, and an expert does the rest. Sell your car
                        securely with an Authorized Purr Dealer.
                    </p>
                    </div>
                </div>
                <div className="stats-item">
                    <div>
                    <h4>The backing of a <br />dealership</h4>
                    <img className="stat-image" src="/marketing-assets/images/icons/car.svg" />
                    <p className="font-graphik">
                        Your Authorized Purr Dealer will schedule test drives, sell your car, and give buyers access to purrks,
                        like warranties, repairs, financing, and insurance.
                    </p>
                    </div>
                </div>
                <div className="stats-item">
                    <div>
                    <h4>More money. <br />No problems.</h4>
                    <img className="stat-image" src="/marketing-assets/images/icons/tag.svg" />
                    <p className="font-graphik">
                        When you sell with Purr, you get full market price for your car. You can earn $2k to $6k more for your car
                        than you would through other selling options.
                    </p>
                    </div>
                </div>
                </div>
                    <div className="text-center mt-4">
                        <Link legacyBehavior href={`/vehicle-lead`} onClick={(event) => {
                            if (user?.type === UserType.DEALER) {
                                event.preventDefault();
                                event.stopPropagation();
                                setIsOpen(true);
                            }
                        }}>
                            <a className="btn btn-purr me-3">Get started</a>
                        </Link>
                    </div>
            </div>
            </section>
            <section className="bg-white buyer-how-it-works">
            <div className="container custom-container">
                <div className="cat">
                <AnimatedComponent path='/marketing-assets/animations/purrcy/cozy.json'/>
                </div>
                <h2 className="alt-header mb-5 text-center"><span className="underline">How it works</span></h2>
                <div className="how-it-works-container">
                <div>
                    <video controls poster="marketing-assets/images/how-to-start-preview.png">
                    <source src="/marketing-assets/videos/how_it_works.mp4" />
                    </video>
                </div>
                <div className="how-it-works-carousel-wrapper">
                    <div className="slider-wrapper">
                    <div className="swiper howItWorksSwiper stats">
                        <Swiper
                        className='w-100'
                        modules={[Navigation, Pagination]}
                        speed={700}
                        slidesPerView={1}
                        spaceBetween={30}
                        slidesPerGroup={1}
                        loop={true}
                        loopFillGroupWithBlank={true}
                        pagination={{
                            el: ".how-it-works-carousel-wrapper .swiper-pagination",
                            clickable: true,
                        }}
                        navigation={{
                            nextEl: ".how-it-works-carousel-wrapper .swiper-button-next",
                            prevEl: ".how-it-works-carousel-wrapper .swiper-button-prev",
                        }}
                        >
                        <SwiperSlide>
                            <div className="slide-container">
                            <div className="texts-container">
                                <h3>First things first </h3>
                                <p className="font-graphik">Sign up, add your vehicle, and partner with a local Authorized Purr
                                Dealer.</p>
                                <img className="number" src="/marketing-assets/images/how-it-works/numbers/Step1.svg" />
                                <div className="illustration-container">
                                <img className="illustration" src="/marketing-assets/images/how-it-works/illustrations/seller-01.png" />
                                </div>
                            </div>
                            </div>
                        </SwiperSlide>
                        <SwiperSlide>
                            <div className="slide-container">
                            <div className="texts-container">
                                <h3>Partner with your Authorized Purr Dealer</h3>
                                <p className="font-graphik">Your Authorized Purr Dealer will inspect your vehicle, take photos, and publish your listing to the marketplace.</p>
                                <img className="number" src="/marketing-assets/images/how-it-works/numbers/Step2.svg" />
                                <div className="illustration-container">
                                <img className="illustration" src="/marketing-assets/images/how-it-works/illustrations/seller-02.png" />
                                </div>
                            </div>
                            </div>
                        </SwiperSlide>
                        <SwiperSlide>
                            <div className="slide-container">
                            <div className="texts-container">
                                <h3>The buyer explores the Purr marketplace</h3>
                                <p className="font-graphik">Buyers shop the Purr marketplace’s exclusive inventory, then schedule a test drive with an Authorized Purr Dealer.</p>
                                <img className="number" src="/marketing-assets/images/how-it-works/numbers/Step3.svg" />
                                <div className="illustration-container">
                                <img className="illustration" src="/marketing-assets/images/how-it-works/illustrations/seller-03.png" />
                                </div>
                            </div>
                            </div>
                        </SwiperSlide>
                        <SwiperSlide>
                            <div className="slide-container">
                            <div className="texts-container">
                                <h3>The test drive</h3>
                                <p className="font-graphik">The buyer meets with your Authorized Purr Dealer at the dealership to
                                test drive your car and discuss dealership purrks.</p>
                                <img className="number" src="/marketing-assets/images/how-it-works/numbers/Step4.svg" />
                                <div className="illustration-container">
                                <img className="illustration" src="/marketing-assets/images/how-it-works/illustrations/seller-04.png" />
                                </div>
                            </div>
                            </div>
                        </SwiperSlide>
                        <SwiperSlide>
                            <div className="slide-container">
                            <div className="texts-container">
                                <h3>Simplest negotiation</h3>
                                <p className="font-graphik">Log in to the Purr marketplace and negotiate directly with your buyer to agree on a purrfect experience.</p>
                                <img className="number" src="/marketing-assets/images/how-it-works/numbers/Step5.svg" />
                                <div className="illustration-container">
                                <img className="illustration" src="/marketing-assets/images/how-it-works/illustrations/seller-05.png" />
                                </div>
                            </div>
                            </div>
                        </SwiperSlide>
                        <SwiperSlide>
                            <div className="slide-container">
                            <div className="texts-container">
                                <h3>Complete the transaction</h3>
                                <p className="font-graphik">All paperwork, payments, repairs, financing, and warranties are
                                handled by the dealer.</p>
                                <img className="number" src="/marketing-assets/images/how-it-works/numbers/Step6.svg" />
                                <div className="illustration-container">
                                <img className="illustration" src="/marketing-assets/images/how-it-works/illustrations/seller-06.png" />
                                </div>
                            </div>
                            </div>
                        </SwiperSlide>
                        <SwiperSlide>
                            <div className="slide-container">
                            <div className="texts-container">
                                <h3>A purrfect experience</h3>
                                <p className="font-graphik">You leave with the best value for your vehicle. Your buyer drives away
                                purring. The dealer gets to put a smile on everybody’s face. <strong>At Purr, everybody wins.
                                    It’s so simple.</strong></p>
                                <img className="number" src="/marketing-assets/images/how-it-works/numbers/Step7.svg" />
                                <div className="illustration-container">
                                <img className="illustration" src="/marketing-assets/images/how-it-works/illustrations/seller-07.png" />
                                </div>
                            </div>
                            </div>
                        </SwiperSlide>
                        </Swiper>
                    </div>
                    <div className="buttons-container">
                        <div className="swiper-button-next"></div>
                        <div className="swiper-button-prev"></div>
                        <div className="swiper-pagination"></div>
                    </div>
                    </div>
                </div>
                </div>
            </div>
            </section>
            <section className="bg-gray challenges">
            <div className="container custom-container">
                <h2 className="alt-header mb-5">What <span className="underline">you’ll love</span> about Purr</h2>
                <div className="stats">
                <div className="stats-item">
                    <div>
                    <h4>Simplest <br />negotiations.</h4>
                    <img className="stat-image" src="/marketing-assets/images/icons/negotiation.svg" />
                    <p className="font-graphik">
                        With Purr, you negotiate directly with the buyer on the Purr platform.
                    </p>
                    </div>
                </div>
                <div className="stats-item">
                    <div>
                    <h4>Get paid quickly <br />and simply.</h4>
                    <img className="stat-image" src="/marketing-assets/images/icons/payment.svg" />
                    <p className="font-graphik">
                        No need to chase buyers for your money. Negotiations and payments are safe and secure with Purr.
                    </p>
                    </div>
                </div>
                <div className="stats-item">
                    <div>
                    <h4>The safest <br />sale.</h4>
                    <img className="stat-image" src="/marketing-assets/images/icons/shield.svg" />
                    <p className="font-graphik">
                        No sketchy transactions on Purr. Your transaction happens securely on our platform, with the support of
                        your Authorized Purr Dealer.
                    </p>
                    </div>
                </div>
                </div>
            </div>
            </section>
            <section className="bg-gray">
            <div className="container custom-container d-flex flex-lg-row flex-column align-items-start">
                <div className="w-50 pe-lg-5">
                <form className="seller-form">
                    <label className="mt-3 mb-2"><strong>What vehicle are you selling?</strong></label>
                    <div className="mb-3">
                    <label className="mt-1 mb-1">Year</label>
                    <select className="form-select" data-type="year" defaultValue="" required>
                        <option disabled value="" hidden>Select Year</option>
                    </select>
                    </div>
                    <div className="mb-3">
                    <label className="mt-1 mb-1">Make</label>
                    <select className="form-select" data-type="make" defaultValue="" required>
                        <option disabled value="" hidden>Select Make</option>
                    </select>
                    </div>
                    <div className="mb-3">
                    <label className="mt-1 mb-1">Model</label>
                    <select className="form-select" data-type="model" defaultValue="" required>
                        <option disabled value="" hidden>Select Model</option>
                    </select>
                    </div>
                    <button type="submit" data-type="submit-button" className="btn btn-purr w-100 mt-2" onClick={(event) => {
                    if (user?.type === UserType.DEALER) {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsOpen(true);
                    }
                    }}>Get started</button>
                </form>
                </div>
                <div className="pt-5 text-right w-50">
                <div className="purrcy_car">
                    <AnimatedComponent path='/marketing-assets/animations/purrcy_car.json'/>
                </div>
                </div>
            </div>
            </section>
            <section className="bg-gray yellow-bottom-circle d-flex align-items-center justify-content-center">
            <div className="container custom-container">
                    <div className="text-center">
                        <h2 className="alt-header">Purr makes it <br/>So simple.</h2>
                        <Link legacyBehavior href={`/vehicle-lead`}>
                            <a className="btn btn-purr dark me-3">Get started</a>
                        </Link>
                    </div>
            </div>
            </section>
        </MarketingLayout>
    </>
  )
}

export default Seller;
